import Style from './generalInfo.module.scss';
import React, { FC, useMemo } from 'react';
import { GeneralPatientInfoProps } from './generalInfo';
import { ExpandedRowHeader } from '../header/expandedRowHeader.component';
import { getCurrentDateLocale } from '../../../application/localisation/localization';
import { IPatientListDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { DateFormats, formatDateRange, formatRelativeDateOnly, localFormat } from '../../../helpers/date-helpers';
import { useScopedTranslation } from '../../../application/localisation/useScopedTranslation';
import { useCommonLocalization } from '../../../application/localisation/useCommonLocalization';
import { AppCheckboxInput } from 'uikit/inputs';
import { Chips } from 'uikit/chips/chips.component';
import { IssueMark } from 'src/components/issue/issueTarget/issueMark.component';
import useApplyPatientFilter from '../../../features/patients/useApplyPatientFilter';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import PropertiesList from 'uikit/propertiesList/PropertiesList';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useGetPatientDeviceBindingsQuery } from 'src/services/api/api-client/PatientQuery';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { ReactComponent as HistoryIcon } from 'src/assets/img/common/history_16.svg';
import { useTranslation } from 'react-i18next';

export const GeneralPatientInfo = React.memo((props: GeneralPatientInfoProps) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.GeneralInfo');
  const { t: patientFiltersLocalizer } = useScopedTranslation('Dashboard.PatientFilters');
  const { hasStudyClaim } = useHasStudyClaims();
  const commonLocalizer = useCommonLocalization();
  const { surveys } = useStudy() ?? {};

  const applyPatientFilter = useApplyPatientFilter();
  const hasSurveys = surveys && surveys.length > 0;

  return (
    <div className={Style.container} data-test-id={'patient-card-general-info'}>
      <div className={Style.tableWrapper}>
        <ExpandedRowHeader
          text={
            <IssueMark
              issueContext={{
                subject: 'Patient',
                linkedPatientUniqId: props.uniqueId,
                topic: 'GeneralInfo',
              }}
              containerClassName={Style.issueMarkContainer}
            >
              {commonLocalizer('Dashboard.PatientAccordion.GeneralInfo.Header')}
            </IssueMark>
          }
        />

        <PropertiesList>
          <DeviceProperty patientDto={props} />
          <PropertiesList.Property
            name={t('PrismaSyncDate')}
            value={
              props.lastTherapyDay
                ? formatRelativeDateOnly(props.lastTherapyDay, Date.now(), getCurrentDateLocale())
                : commonLocalizer('Common_Never')
            }
          />
          {props.contractSignedAt && (
            <PropertiesList.Property
              name={t('ContractSignDate')}
              value={localFormat(props.contractSignedAt, DateFormats.longDate)}
            />
          )}
          {!props.isStudyFinished && props.isStudyStarted && (
            <PropertiesList.Property
              name={t('PatientFilters')}
              value={
                <div className={Style.checkboxWrapper}>
                  <AppCheckboxInput
                    label={patientFiltersLocalizer('IsClinic')}
                    // We use "checked" instead of "defaultChecked" there,
                    // because we update this state from the "Stationäre Aufenthalt" form
                    checked={props?.filters?.isClinic}
                    onChange={(e) => applyPatientFilter(props.id, 'isClinic', e.target.checked)}
                    readOnly={!hasStudyClaim(StudyRoleClaimValue.PatientCreate)}
                  />
                  <AppCheckboxInput
                    label={patientFiltersLocalizer('IsControlled')}
                    defaultChecked={props?.filters?.isControlled}
                    onChange={(e) => applyPatientFilter(props.id, 'isControlled', e.target.checked)}
                    readOnly={!hasStudyClaim(StudyRoleClaimValue.PatientCreate)}
                  />
                  {hasSurveys && (
                    <AppCheckboxInput
                      label={patientFiltersLocalizer('NoApp')}
                      defaultChecked={props?.filters?.hasNoApp}
                      onChange={(e) => applyPatientFilter(props.id, 'hasNoApp', e.target.checked)}
                      readOnly={!hasStudyClaim(StudyRoleClaimValue.PatientCreate)}
                    />
                  )}
                </div>
              }
            />
          )}
          {!!props.tags &&
            Object.entries(props.tags).map(([key, values]: [string, string[]]) => (
              <PropertiesList.Property
                key={key}
                testId="tag"
                nameTestId="tag-key"
                valueTestId="tag-value"
                name={key}
                value={
                  <div className={Style.tagValueWrapper}>
                    {values.map((value) => (
                      <Chips key={value} checked text={value} />
                    ))}
                  </div>
                }
              />
            ))}
        </PropertiesList>
      </div>
    </div>
  );
});

const DeviceProperty: FC<{ patientDto: IPatientListDto }> = ({ patientDto }) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.GeneralInfo');
  const { t: commonLocalizer } = useTranslation();
  const { data: deviceBindings, isInitialLoading } = useGetPatientDeviceBindingsQuery(patientDto.id, {
    refetchOnMount: 'always',
  });

  const valueWithStyle: { value: string | null; className: string | undefined } = useMemo(() => {
    if (isInitialLoading) return { value: commonLocalizer('Common_loading'), className: Style.greyValue };
    if (!!patientDto.deviceSerialNumber) return { value: patientDto.deviceSerialNumber, className: undefined };
    if (!deviceBindings?.length) return { value: t('DeviceNoAssignedYet'), className: Style.greyValue };
    return { value: t('DeviceUnassigned'), className: undefined };
  }, [isInitialLoading, commonLocalizer, patientDto.deviceSerialNumber, deviceBindings?.length, t]);

  return (
    <PropertiesList.Property
      name={t('DeviceSerial')}
      value={valueWithStyle.value}
      valueClassName={valueWithStyle.className}
      rightElement={
        !deviceBindings?.length ? null : (
          <Tooltip
            hostStyles={Style.historyIcon}
            tooltipContent={
              <div className={Style.tooltipContent}>
                <div className={Style.tooltipHeader}>{t('DeviceBindingsTooltipHeader')}</div>
                <div className={Style.bindingsList}>
                  {deviceBindings.map((db) => (
                    <div key={db.deviceSerialNumber} className={Style.bindingItem}>
                      <div>{db.deviceSerialNumber}</div>
                      <div className={Style.bindingDate}>
                        {formatDateRange(db.assignedAt, db.unassignedAt, '', t('DeviceNotUnassignedYet'))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <HistoryIcon />
          </Tooltip>
        )
      }
    />
  );
};
